import { withErrorBoundary } from "@sentry/nextjs";
import cn from "classnames";
import { gql } from "graphql-request";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { CSSProperties, useCallback, useEffect, useState, VFC } from "react";
import styled from "styled-components";
import { Loader, Markdown, SupTitle } from "components";
import { usePipelineRoutingLink } from "components/Molecules/PipelineRoutingLinkButton";
import { useAnchor } from "hooks";
import { trackEvent } from "lib/tracking/segment";
import { TrackEventDataType } from "lib/tracking/types";
import { DataTestId } from "types/enums/testId";
import { TaxCalculatorContent } from "types/graphql/generated";

const GuiTaxCalculator: any = dynamic(
  async () => {
    // @ts-ignore
    const mod = await import("@guideline-app/calculators");

    return mod.TaxCalculator;
  },
  {
    loading: () => (
      <StyledTaxCalculator className="TaxCalculator__loading">
        <Loader />
      </StyledTaxCalculator>
    ),
    ssr: false,
  }
);

interface Props {
  className?: string;
  content: TaxCalculatorContent;
}

const TaxCalculator: VFC<Props> = withErrorBoundary(
  ({ content: { calculatorSupTitle, calculatorTitle, calculatorDescription, cta } }) => {
    useAnchor("tax-calculator");
    const [mounted, setMounted] = useState(false);
    const [searchParams, setSearchParams] = useState("");

    useEffect(() => {
      setMounted(true);

      const search = window?.location?.search;

      setSearchParams(search ? `?${search}` : "");
    }, []);

    const { asPath } = useRouter();
    const path = asPath.split(/[?#]/)[0];
    const backgroundColor = path === "/integrations/gusto/taxsavings" ? "#FAF6F4" : "unset";
    const handleTrackEvent = useCallback((event: string, data: TrackEventDataType) => trackEvent(event, data), []);

    let ctaOverride = cta ?? {};
    // Always parse the href to get the correct URL query params and route GSF data
    const { href: formattedHref, onClick } = usePipelineRoutingLink({ href: ctaOverride?.href ?? "/pipeline/basic" });

    // Append any search params to the href
    if (searchParams) {
      ctaOverride.href = `/pipeline/basic${searchParams}`;
    }

    // cta.href defaults to /pipeline/basic, so ovedrride cta.href is not defined
    if (!cta?.href) {
      ctaOverride.href = formattedHref;
    }

    if (!mounted) {
      // wait for URL query params
      return null;
    }

    return (
      <StyledTaxCalculator
        className={cn("tax-calculator", { TaxCalculator__hasSupTitle: !!calculatorSupTitle })}
        style={{ "--background-color": backgroundColor } as CSSProperties}
      >
        <div className="TaxCalculator__innerContainer Content" data-testid={DataTestId.TaxCalculatorContainer}>
          <div className="TaxCalculator__textContent">
            {calculatorSupTitle && <SupTitle>{calculatorSupTitle}</SupTitle>}

            <h2 className="TaxCalculator__title Font_48_54_600 lg:Font_40_48_600">{calculatorTitle}</h2>
            <Markdown className="TaxCalculator__description Color_secondary Font_24_32_450">
              {calculatorDescription}
            </Markdown>
          </div>
          <div className="TaxCalculator__calculatorContainer">
            {mounted ? <GuiTaxCalculator trackEvent={handleTrackEvent} cta={{ ...ctaOverride, onClick }} /> : null}
          </div>
        </div>
      </StyledTaxCalculator>
    );
  },
  {}
);

const StyledTaxCalculator = styled.section`
  padding-top: 100px;
  margin-top: -100px;
  background-color: var(--background-color);

  .TaxCalculator__innerContainer {
    display: flex;
  }

  .TaxCalculator__title {
    width: 364px;
    max-width: 100%;
  }

  &.TaxCalculator__hasSupTitle {
    .TaxCalculator__title {
      margin-top: 32px;
    }
  }

  .TaxCalculator__description {
    width: 435px;
    max-width: 100%;
    margin-top: 24px;
  }

  .TaxCalculator__calculatorContainer {
    position: relative;
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-left: 64px;
    padding: 32px;
    background:
      radial-gradient(85.93% 39.2% at 91.09% 52.37%, #ffcd85 0%, rgb(252 207 143 / 0%) 100%)
        /* warning: gradient uses a rotation that is not supported by CSS and may not behave as expected */,
      radial-gradient(106.86% 76.15% at 47.2% 90.29%, #5532fa 6.99%, #efa085 66.87%, #fcdac4 100%)
        /* warning: gradient uses a rotation that is not supported by CSS and may not behave as expected */,
      radial-gradient(
          59.96% 84.56% at 108.46% 114.67%,
          rgb(126 100 251 / 75%) 14.44%,
          rgb(255 205 133 / 75%) 59.75%,
          rgb(246 243 241 / 0%) 100%
        )
        /* warning: gradient uses a rotation that is not supported by CSS and may not behave as expected */;
    background-blend-mode: normal, normal, overlay, normal;
    min-height: 800px;
  }

  .TaxCalculator__loading {
    align-items: center;
    background-color: #fff;
    border-radius: 8px;
    bottom: 32px;
    display: flex;
    justify-content: center;
    height: calc(100% - 64px);
    margin: 0 auto;
    padding: 0;
    position: absolute;
    top: 34px;
    width: calc(100% - 64px);
  }

  .tax-calc {
    padding: 0;
  }

  @media (min-width: 501px) {
    .form__date-picker--open {
      margin-left: -64px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .TaxCalculator__innerContainer {
      flex-direction: column;
    }

    .TaxCalculator__title {
      width: 100%;
    }

    .TaxCalculator__description {
      width: 900px;
    }

    .TaxCalculator__calculatorContainer {
      max-width: 717px;
      margin-left: 0;
      margin-top: 48px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .TaxCalculator__description {
      width: 100%;
    }

    .TaxCalculator__calculatorContainer {
      max-width: 701px;
      padding-left: 24px;
      padding-right: 24px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .TaxCalculator__description {
      width: 100%;
    }

    .TaxCalculator__calculatorContainer {
      max-width: unset;
      margin-top: 40px;
      margin-left: -24px;
      margin-right: -24px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    .TaxCalculator__calculatorContainer {
      padding: 12px;
    }

    .TaxCalculator__loading {
      top: 12px;
      height: calc(100% - 24px);
      width: calc(100% - 24px);
    }
  }
`;

gql`
  fragment TaxCalculatorContent on ComponentCalculatorTaxCalculator {
    calculatorDescription
    calculatorSupTitle
    calculatorTitle
    cta {
      external
      href
      text
    }
  }
`;

export { TaxCalculator };
export type { Props as TaxCalculatorProps };
